/**
 * Resource Request Planning Engine
 *
 * Given a ResourceRequest (hours budget + date range + role/skills),
 * this engine calls the backend plan-availability API which computes daily
 * availability breakdowns from fresh DB data for the whole active resource pool
 * (no role/skills/location scoring — that is a frontend concern per PRD §6.3).
 *
 * The frontend then ranks the returned resources by role/skills match (via the
 * client-side recommendation engine) and builds 3 concrete PlanScenarios using
 * different optimisation strategies from the server-returned data:
 *
 *   1. single-resource  — concentrate all hours on the single best match
 *   2. spread-load      — spread across 2-3 resources to balance capacity
 *   3. fastest-start    — prioritise earliest availability over perfect fit
 *
 * Availability computation lives on the backend; the "perfect resource"
 * role/skills ranking lives here on the frontend.
 */

import { Absence, BankHoliday, Resource, Booking, WorkingHoursProfile } from '@/types';
import {
  ResourceRequest,
  PlanPreviewScenario,
  PlanScenario,
  ProposedBookingSlot,
  DailyAllocation,
} from '@/types/resourceRequest';
import {
  resourceAvailabilityApi,
  type DailyAvailabilitySlot,
  type DailyUtilisationSlot,
  type MonthlyUtilisationSlot,
  type PlanAvailabilityRequest,
  type PlanAvailabilityResponse,
  type PlanAvailabilitySummaryResponse,
  type ResourcePlanResult,
  type ResourcePlanSummary,
} from '@/services/api';
import { featureFlags } from '@/lib/feature-flags';
import {
  MeasuredPool,
  orderedTaker,
  pooledHoursUpTo,
  takeRanked,
  type RankComparator,
  type ShortlistCandidate,
} from '@/lib/planShortlist';
import { computeRecommendations, normaliseRole, skillsCoverageFor, splitRequestedSkills } from '@/lib/recommendationEngine';
import { consumesCapacity } from '@/lib/bookingCapacity';
import { roundHours, sumHours } from '@/lib/hours';
import { capacityLookup, clampSlotToCapacity, type CapacityLookup } from '@/lib/planSlotCapacity';
import { unavailableDaysByResourceId as unavailableDaysForResources } from '@/lib/resourceUnavailableDays';
import { effectiveWorkingProfile, type UnavailableDay } from '@/lib/slotEditValidation';
import {
  dayIntervalsFromWindows,
  minutesToHHMM,
  subtractIntervals,
  type DayInterval,
} from '@/lib/dayAvailability';

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * 'YYYY-MM-DD' for the calendar day the Date falls on, read in local time.
 * Dates here are local midnights, so toISOString() would report the previous day
 * for any positive UTC offset (e.g. 1 Aug in BST -> "2026-07-31"), shifting whole
 * plan windows a day earlier and dropping the last day of the month.
 */
function iso(d: Date) {
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

function ym(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(ymStr: string) {
  const [y, m] = ymStr.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

function startOfMonth(ymStr: string) {
  const [y, m] = ymStr.split('-').map(Number);
  return new Date(y, m - 1, 1);
}

function endOfMonth(ymStr: string) {
  const [y, m] = ymStr.split('-').map(Number);
  return new Date(y, m, 0);
}

export function buildPlanPreviewScenario(
  scenario: PlanScenario,
  slots: ProposedBookingSlot[],
  request: Pick<ResourceRequest, 'id' | 'projectName' | 'role'>,
): PlanPreviewScenario {
  return {
    scenario: {
      ...scenario,
      slots,
    },
    requestId: request.id,
    requestProjectName: request.projectName,
    requestRole: request.role,
  };
}

/**
 * Parse a request date into local midnight.
 *
 * Date-only ISO strings ('YYYY-MM-DD') are parsed by `new Date()` as UTC
 * midnight, so reading them back with the local getters used throughout this
 * module (`ym`, `startOfMonth`, `monthsInRange`, the daily breakdown loop)
 * shifts the day — and potentially the month — for viewers behind UTC. Anchor
 * them to the local calendar day instead so the whole pipeline agrees.
 */
function parseRequestDate(value: string): Date {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  const parsed = new Date(value);
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/** Local midnight of the calendar day a Date falls on. */
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Parse an availability slot's 'YYYY-MM-DD' date as local midnight. */
function parseSlotDate(date: string): Date {
  return new Date(`${date}T00:00:00`);
}

function monthsInRange(start: Date, end: Date): string[] {
  const months: string[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const endYm = ym(end);
  while (ym(cur) <= endYm) {
    months.push(ym(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
}

function slotId() {
  return `slot-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Split `budget` hours as evenly as the resources' free time allows.
 *
 * Everyone with the capacity for it gets the same share; a resource whose
 * availability cannot absorb an equal share takes all of it, and the hours it
 * could not take are re-split evenly between the resources that still have room
 * (so the most available resource ends up with the most hours only when the
 * capacity is genuinely uneven):
 *
 *   splitEvenly(30, [40, 40, 40]) → [10, 10, 10]
 *   splitEvenly(30, [40,  4, 40]) → [13, 4, 13]
 *
 * `priorHours` levels the split across several budgets (the months of a plan):
 * what counts as even is the resource's running total, so hours a resource
 * could not be given in an earlier month are made up to it later:
 *
 *   splitEvenly(50, [80, 80], [43, 0]) → [3.5, 46.5]
 *
 * Shares are returned in the order of `avails` and never exceed either the
 * resource's availability or, in total, the budget.
 */
export function splitEvenlyByAvailability(
  budget: number,
  avails: number[],
  priorHours: number[] = [],
): number[] {
  const prior = avails.map((_, i) => priorHours[i] ?? 0);
  const shares = avails.map(() => 0);
  let open = avails.map((_, i) => i).filter(i => avails[i] > 0);
  let remaining = budget;

  while (remaining > 0.05 && open.length > 0) {
    // The running total every open resource would reach if the hours left were
    // used purely to level them up with each other.
    const level =
      (remaining + open.reduce((s, i) => s + prior[i] + shares[i], 0)) / open.length;

    // Anyone already past that total is ahead of the rest and sits this round
    // out, which raises the level for the resources still behind.
    const ahead = open.filter(i => prior[i] + shares[i] >= level);
    if (ahead.length === open.length) break;
    if (ahead.length > 0) {
      open = open.filter(i => !ahead.includes(i));
      continue;
    }

    const capped = open.filter(i => prior[i] + avails[i] <= level);
    if (capped.length === 0) {
      for (const i of open) shares[i] = level - prior[i];
      remaining = 0;
      break;
    }

    for (const i of capped) {
      remaining -= avails[i] - shares[i];
      shares[i] = avails[i];
    }
    open = open.filter(i => !capped.includes(i));
  }

  const rounded = shares.map(roundHours);
  // Rounding to a tenth of an hour can lose or gain a tenth against the budget;
  // it is settled on whoever is furthest behind and still has room (or, when a
  // tenth has to come back off, on the largest share).
  let drift = roundHours(Math.min(budget, avails.reduce((s, a) => s + a, 0)) - rounded.reduce((s, h) => s + h, 0));
  while (drift >= 0.1) {
    const i = largestBy(rounded, (h, idx) => (avails[idx] - h >= 0.1 ? -(prior[idx] + h) : -Infinity));
    if (i == null || avails[i] - rounded[i] < 0.1) break;
    rounded[i] = roundHours(rounded[i] + 0.1);
    drift = roundHours(drift - 0.1);
  }
  while (drift <= -0.1) {
    const i = largestBy(rounded, h => h);
    if (i == null || rounded[i] < 0.1) break;
    rounded[i] = roundHours(rounded[i] - 0.1);
    drift = roundHours(drift + 0.1);
  }

  return rounded;
}

function largestBy(values: number[], score: (value: number, index: number) => number): number | null {
  let best: number | null = null;
  let bestScore = -Infinity;
  for (let i = 0; i < values.length; i++) {
    const candidate = score(values[i], i);
    if (candidate > bestScore) {
      best = i;
      bestScore = candidate;
    }
  }
  return best;
}

/** A day's line in a slot's human-readable breakdown, e.g. "8 Jul: 6h". */
function dayBreakdownLabel(allocation: DailyAllocation): string {
  const day = new Date(`${allocation.date.slice(0, 10)}T00:00:00Z`);
  const label = day.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return `${label}: ${allocation.hours}h`;
}

/**
 * Backend availability slots are serialised as wall-clock timestamps with no
 * timezone designator (e.g. "2026-07-08T08:00:00.0000000"). Anchor them to UTC
 * so parsing and rendering are timezone-independent. Strings that already carry
 * a designator (Z or ±hh:mm) are returned unchanged.
 */
function toUtcIso(iso?: string): string | undefined {
  if (!iso) return undefined;
  if (/([zZ]|[+-]\d{2}:?\d{2})$/.test(iso)) return iso;
  return `${iso}Z`;
}

/**
 * Convert server-returned DailyAvailabilitySlot[] into the distribution shape
 * used by the scenario builders, capping hours to assignedHours.
 *
 * Slots are sorted chronologically so the plan fills from the earliest
 * available date forward — producing the earliest feasible start date and
 * allocating a day's full hours before spilling to the next day.
 */
export function distributeFromServerSlots(
  serverSlots: DailyAvailabilitySlot[],
  assignedHours: number,
  businessHoursStart: number,
  businessHoursEnd: number,
): { startDate: Date; endDate: Date; actualHours: number; dailyBreakdown: string[]; dailyAllocations: DailyAllocation[] } | null {
  if (serverSlots.length === 0 || assignedHours <= 0) return null;

  // Sort slots chronologically so the plan starts at the earliest available
  // date and fills each day before moving to the next.
  const sorted = [...serverSlots].sort((a, b) => {
    const dateCmp = a.date.localeCompare(b.date);
    if (dateCmp !== 0) return dateCmp;
    return a.startTime.localeCompare(b.startTime);
  });

  let remaining = assignedHours;
  const picked: { slot: DailyAvailabilitySlot; roundedHours: number }[] = [];

  for (const slot of sorted) {
    if (remaining <= 0) break;
    const hoursThisDay = Math.min(slot.hours, remaining);
    const roundedHours = roundHours(hoursThisDay);
    if (roundedHours <= 0) continue;

    picked.push({ slot, roundedHours });
    remaining -= hoursThisDay;
  }

  if (picked.length === 0) return null;

  const breakdown: string[] = [];
  const allocations: DailyAllocation[] = [];
  let firstDay: Date | null = null;
  let lastDay: Date | null = null;

  for (const { slot, roundedHours } of picked) {
    const dayDate = new Date(slot.date + 'T00:00:00Z');
    if (!firstDay) firstDay = dayDate;
    lastDay = dayDate;

    // Use the slot's actual startTime and endTime — they reflect the exact
    // available window that does not overlap with existing bookings.
    // Fall back to businessHoursStart/End if no explicit times.
    //
    // The backend serialises these as wall-clock timestamps without a timezone
    // designator (e.g. "2026-07-08T08:00:00.0000000"). Interpret them as UTC so
    // the whole pipeline (parsing here, the toISOString() end, the businessHours
    // bounds below, and the UTC-based rendering in the drawer) agree. Without
    // this, `new Date(naive)` parses as browser-local and the displayed window
    // shifts — even inverting — by the viewer's UTC offset.
    const endHH = String(businessHoursEnd).padStart(2, '0');
    const fallbackStartHH = String(businessHoursStart).padStart(2, '0');
    const startTime = toUtcIso(slot.startTime) || `${slot.date}T${fallbackStartHH}:00:00Z`;

    const startMs = new Date(startTime).getTime();
    let endMs = startMs + roundedHours * 3600000;

    // Cap endTime at the slot's own endTime (end of free window) and
    // at businessHoursEnd — ensures we never overlap into a booking.
    const slotEndIso = toUtcIso(slot.endTime);
    if (slotEndIso) {
      const slotEnd = new Date(slotEndIso).getTime();
      if (endMs > slotEnd) endMs = slotEnd;
    }
    const bizEnd = new Date(`${slot.date}T${endHH}:00:00Z`).getTime();
    if (endMs > bizEnd) {
      endMs = bizEnd;
    }
    const endTime = new Date(endMs).toISOString();

    const allocation: DailyAllocation = { date: slot.date, hours: roundedHours, startTime, endTime };
    breakdown.push(dayBreakdownLabel(allocation));
    allocations.push(allocation);
  }

  if (!firstDay || !lastDay) return null;

  return {
    startDate: firstDay,
    endDate: lastDay,
    actualHours: roundHours(assignedHours - Math.max(0, remaining)),
    dailyBreakdown: breakdown,
    dailyAllocations: allocations,
  };
}

/** What a scenario builder needs to write a slot, once capacity has been applied. */
interface PlannedSlotShape {
  /** Inclusive slot range, 'yyyy-MM-dd'. */
  startDate: string;
  endDate: string;
  hours: number;
  dailyAllocations: DailyAllocation[];
  dailyBreakdown: string[];
}

/**
 * The slot a distribution becomes: its hours reduced to the capacity the
 * validator will measure for the same resource and range, with the trailing day
 * windows trimmed so the breakdown still sums to the total.
 *
 * Returns null when the range has no capacity left at all, so the caller can
 * carry the hours to the next month or resource instead of proposing a slot
 * that cannot be allocated.
 */
function finaliseDistribution(
  distribution: { startDate: Date; endDate: Date; actualHours: number; dailyAllocations: DailyAllocation[] },
  resourceId: number,
  capacityFor: CapacityLookup,
): PlannedSlotShape | null {
  const clamped = clampSlotToCapacity(
    {
      startDate: iso(distribution.startDate),
      endDate: iso(distribution.endDate),
      hours: roundHours(distribution.actualHours),
      dailyBreakdown: distribution.dailyAllocations,
    },
    capacityFor(String(resourceId)),
  );
  if (!clamped || clamped.hours <= 0) return null;

  const dailyAllocations = clamped.dailyBreakdown ?? [];
  return {
    startDate: clamped.startDate,
    endDate: clamped.endDate,
    hours: clamped.hours,
    dailyAllocations,
    dailyBreakdown: dailyAllocations.map(dayBreakdownLabel),
  };
}

/**
 * Availability reduced to the hours the drawer's validator will actually allow:
 * every day keeps only what the resource still has free on it, and a resource
 * with nothing free anywhere in the window leaves the pool.
 *
 * The backend sizes availability from the free gaps in a day's work window,
 * while the validator judges a day against the resource's contracted hours less
 * everything already booked on it. Where the two disagree — a day booked past
 * its contracted hours, or bookings the availability query does not attribute to
 * the resource — the planner used to propose the gap anyway and the drawer then
 * refused it as "fully booked". Measuring the pool with the validator's own
 * capacity keeps those days (and those resources) out of the suggestion, so the
 * plan moves on to the next resource of the same role, then to a skills match.
 */
function withMeasuredCapacity(
  results: ResourcePlanResult[],
  capacityFor: CapacityLookup,
  blockedIntervalsFor: BlockedIntervalLookup,
  windowStart: Date,
  windowEnd: Date,
): ResourcePlanResult[] {
  const from = startOfDay(windowStart);
  const to = startOfDay(windowEnd);
  const kept: ResourcePlanResult[] = [];

  for (const result of results) {
    const capacity = capacityFor(String(result.resourceId));
    const freeByDay = new Map<string, number>();
    const breakdown: DailyAvailabilitySlot[] = [];

    const chronological = [...result.dailyBreakdown].sort((a, b) => {
      const byDate = a.date.localeCompare(b.date);
      return byDate !== 0 ? byDate : a.startTime.localeCompare(b.startTime);
    });

    for (const slot of chronological) {
      const day = parseSlotDate(slot.date);
      if (day < from || day > to) continue;
      if (!freeByDay.has(slot.date)) freeByDay.set(slot.date, capacity.hoursOnDay(slot.date));

      for (const part of aroundAbsences(slot, blockedIntervalsFor(String(result.resourceId), slot.date))) {
        const free = freeByDay.get(slot.date) ?? 0;
        if (free <= 0) break;
        const hours = Math.min(part.availableHours, free);
        if (hours <= 0) continue;

        breakdown.push(hours >= part.availableHours ? part : shortenSlot(part, roundHours(hours)));
        freeByDay.set(slot.date, free - hours);
      }
    }

    const totalAvailableHours = roundHours(
      breakdown.reduce((sum, slot) => sum + slot.availableHours, 0),
    );
    if (totalAvailableHours <= 0) continue;

    kept.push({ ...result, dailyBreakdown: breakdown, totalAvailableHours });
  }

  return kept;
}

/** The parts of a day an absence takes off a resource, keyed by resource and day. */
type BlockedIntervalLookup = (resourceId: string, date: string) => DayInterval[];

/**
 * An availability window split around the parts of the day an absence takes.
 *
 * A half-day absence leaves the other half bookable, and where in the day that
 * half sits matters: trimming a full-day window down to an AM absence's leftover
 * hours would still propose the morning it takes. The window is returned as it
 * came when nothing is blocked, or when the absence's placement is unknown (a
 * full day, or a half day whose half was not recorded) — the hours it loses are
 * taken off by the per-day capacity cap instead.
 */
function aroundAbsences(
  slot: DailyAvailabilitySlot,
  blocked: DayInterval[],
): DailyAvailabilitySlot[] {
  if (blocked.length === 0) return [slot];
  const window = dayIntervalsFromWindows([slot]);
  if (window.length === 0) return [slot];

  return subtractIntervals(window, blocked)
    .map(part => sliceSlot(slot, part))
    .filter(part => part.availableHours > 0);
}

/** An availability window narrowed to one span of its day. */
function sliceSlot(slot: DailyAvailabilitySlot, part: DayInterval): DailyAvailabilitySlot {
  const hours = roundHours((part.endMinutes - part.startMinutes) / 60);
  return {
    ...slot,
    hours,
    availableHours: hours,
    startTime: atTimeOfDay(slot.startTime, part.startMinutes),
    endTime: atTimeOfDay(slot.endTime, part.endMinutes),
  };
}

/** A timestamp moved to another time of the same day, its shape kept. */
function atTimeOfDay(stamp: string, minutes: number): string {
  const parsed = /^(\d{4}-\d{2}-\d{2})([T ])\d{2}:\d{2}(.*)$/.exec(stamp);
  if (!parsed) return stamp;
  return `${parsed[1]}${parsed[2]}${minutesToHHMM(minutes)}${parsed[3]}`;
}

/** An availability window shortened to the hours it keeps. */
function shortenSlot(slot: DailyAvailabilitySlot, hours: number): DailyAvailabilitySlot {
  const start = new Date(toUtcIso(slot.startTime) ?? slot.startTime);
  const endTime = isNaN(start.getTime())
    ? slot.endTime
    : new Date(start.getTime() + hours * 3_600_000).toISOString();
  return { ...slot, hours, availableHours: hours, endTime };
}

/**
 * Filter server slots to only those within a specific month range, clamped to
 * the requested window.
 *
 * `rangeStart` matters for the FIRST month of the range: without it a mid-month
 * requested start (e.g. 15 July) would still return slots from the 1st, so the
 * proposed booking could begin before the date the requester asked for.
 */
function slotsForMonth(
  slots: DailyAvailabilitySlot[],
  monthKey: string,
  rangeStart?: Date,
  rangeEnd?: Date,
): DailyAvailabilitySlot[] {
  const mStart = startOfMonth(monthKey);
  const mEnd = endOfMonth(monthKey);
  const lower = rangeStart && startOfDay(rangeStart) > mStart ? startOfDay(rangeStart) : mStart;
  const upper = rangeEnd && startOfDay(rangeEnd) < mEnd ? startOfDay(rangeEnd) : mEnd;
  return slots.filter(s => {
    const d = parseSlotDate(s.date);
    return d >= lower && d <= upper;
  });
}

// ── Local fallback: compute ResourcePlanResult[] from frontend mock data ─────
// Used when the backend is unavailable (e.g. demo / frontend-only mode).
// Scores resources by role+skills match, then builds a day-by-day breakdown
// of available hours by subtracting existing bookings from the working profile.

interface LocalPlanInput {
  request: ResourceRequest;
  resources: Resource[];
  existingBookings: Booking[];
  globalWorkingProfile: WorkingHoursProfile;
  businessHoursStart: number;
  businessHoursEnd: number;
  requestStart: Date;
  requestEnd: Date;
}

function computeLocalPlanResult(input: LocalPlanInput): ResourcePlanResult[] {
  const { request, resources, existingBookings, globalWorkingProfile, businessHoursStart, businessHoursEnd, requestStart, requestEnd } = input;

  const businessWindowHours = businessHoursEnd - businessHoursStart;
  const { required: requiredSkills, preferred: preferredSkills } =
    splitRequestedSkills(request.skills, request.skillsDetailed);

  // Day names mapping (Date.getDay() → profile key)
  const DAY_KEYS: (keyof WorkingHoursProfile)[] = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
  ];

  // Role match (exact or substring overlap)
  function matchesRole(r: Resource): boolean {
    const requestRoleLower = request.role.toLowerCase();
    const resourceRoleLower = (r.role ?? '').toLowerCase();
    return (
      resourceRoleLower === requestRoleLower ||
      requestRoleLower.split(' ').some(word => resourceRoleLower.includes(word)) ||
      resourceRoleLower.split(' ').some(word => requestRoleLower.includes(word))
    );
  }

  // ── Score each resource using the soft-signal model ──────────────────────
  function scoreResource(r: Resource): number {
    let signals = 0;

    if (matchesRole(r)) signals++;

    // Skills match — met once any REQUIRED skill is covered; preferred skills
    // never withhold the signal. Not a criterion at all when the request
    // specifies no skills: it is dropped rather than auto-passed.
    if (requiredSkills.length > 0 && skillsCoverageFor(r, requiredSkills) > 0) {
      signals++;
    } else if (requiredSkills.length === 0 && preferredSkills.length > 0) {
      signals++;
    }

    // Utilisation — always met in local fallback (no prior utilisation data)
    signals++;

    return signals;
  }

  // ── Build daily breakdown for a resource in [requestStart, requestEnd] ──
  function buildDailyBreakdown(r: Resource): DailyAvailabilitySlot[] {
    const profile = effectiveWorkingProfile(r, globalWorkingProfile);

    // Collect busy intervals from existing bookings for this resource
    const resourceBookings = existingBookings.filter(
      b => (b.resourceId === r.id || (b.resourceIds ?? []).includes(r.id)) && consumesCapacity(b),
    );

    const slots: DailyAvailabilitySlot[] = [];

    const cur = new Date(requestStart.getFullYear(), requestStart.getMonth(), requestStart.getDate());
    const endDay = new Date(requestEnd.getFullYear(), requestEnd.getMonth(), requestEnd.getDate());

    while (cur <= endDay) {
      const dayKey = DAY_KEYS[cur.getDay()];
      const contractedHours = (profile as unknown as Record<string, number>)[dayKey as string] ?? 0;

      if (contractedHours > 0) {
        const dateStr = iso(cur);
        // Clamp the slot to the configured business-hours window so
        // generated plan bookings start/end within business hours.
        const startHH = String(businessHoursStart).padStart(2, '0');
        const endHH = String(businessHoursEnd).padStart(2, '0');
        const dayStart = `${dateStr}T${startHH}:00:00Z`;
        const dayEnd = `${dateStr}T${endHH}:00:00Z`;

        // Sum booked hours on this day for this resource
        let bookedHours = 0;
        for (const bk of resourceBookings) {
          try {
            const bkStart = new Date(bk.startTime);
            const bkEnd = new Date(bk.endTime);
            const dayMidnight = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate());
            const nextMidnight = new Date(dayMidnight.getTime() + 86400000);
            if (bkEnd > dayMidnight && bkStart < nextMidnight) {
              const overlapStart = Math.max(bkStart.getTime(), dayMidnight.getTime());
              const overlapEnd = Math.min(bkEnd.getTime(), nextMidnight.getTime());
              bookedHours += (overlapEnd - overlapStart) / 3600000;
            }
          } catch { /* skip malformed bookings */ }
        }

        const availableHours = Math.min(
          Math.max(0, contractedHours - bookedHours),
          businessWindowHours,
        );

        slots.push({
          date: dateStr,
          startTime: dayStart,
          endTime: dayEnd,
          hours: availableHours,
          availableHours,
        });
      }

      cur.setDate(cur.getDate() + 1);
    }

    return slots;
  }

  // ── Build and rank results ─────────────────────────────────────────────
  const results: ResourcePlanResult[] = [];
  // Skills coverage keyed by the id used in `results`, which is not always the
  // frontend resource id (non-numeric ids fall back to a positional index).
  const coverageById = new Map<number, { required: number; preferred: number }>();

  // Required skills are an OR and a hard condition: a resource holding none of
  // them meets none of the request's skills criteria and is never proposed,
  // however well its role matches. Holders of a preferred skill only are a
  // reserve, added below while the required-skill holders cannot cover the
  // requested hours between them.
  const reserve: ResourcePlanResult[] = [];
  for (const r of resources) {
    if (r.id === 'unallocated') continue;
    const requiredCoverage = skillsCoverageFor(r, requiredSkills);
    const preferredCoverage = skillsCoverageFor(r, preferredSkills);
    const isReserve = requiredSkills.length > 0 && requiredCoverage === 0;
    if (isReserve && (preferredSkills.length === 0 || preferredCoverage === 0)) continue;

    const score = scoreResource(r);

    const dailyBreakdown = buildDailyBreakdown(r);
    const totalAvailable = sumHours(dailyBreakdown.map(s => s.availableHours));
    if (totalAvailable <= 0) continue;

    // Resource IDs are strings like '1', '17' — parse to number; fall back to index
    const numericId = parseInt(r.id, 10);
    const resourceId = isNaN(numericId) ? results.length + reserve.length + 1 : numericId;
    coverageById.set(resourceId, {
      required: requiredCoverage,
      preferred: preferredCoverage,
    });
    (isReserve ? reserve : results).push({
      resourceId,
      resourceName: r.name,
      resourceRole: r.role ?? '',
      matchScore: score,
      totalAvailableHours: totalAvailable,
      dailyBreakdown,
    });
  }

  reserve.sort((a, b) =>
    ((coverageById.get(b.resourceId)?.preferred ?? 0) - (coverageById.get(a.resourceId)?.preferred ?? 0))
    || (b.totalAvailableHours - a.totalAvailableHours));
  let pooledHours = results.reduce((sum, r) => sum + r.totalAvailableHours, 0);
  for (const r of reserve) {
    if (pooledHours >= request.totalHoursRequested) break;
    results.push(r);
    pooledHours += r.totalAvailableHours;
  }

  // Soft signals first, then required-skill coverage so a resource missing a
  // required skill sinks below one that holds it — without ever leaving the pool.
  // Preferred coverage only breaks the remaining ties.
  results.sort((a, b) => {
    if (b.matchScore !== a.matchScore) return b.matchScore - a.matchScore;
    const ca = coverageById.get(a.resourceId);
    const cb = coverageById.get(b.resourceId);
    const requiredDiff = (cb?.required ?? 0) - (ca?.required ?? 0);
    if (requiredDiff !== 0) return requiredDiff;
    return (cb?.preferred ?? 0) - (ca?.preferred ?? 0);
  });

  return results;
}

/**
 * Number of bookings a set of proposed slots will actually create.
 *
 * A slot is a per-resource, per-month allocation, not a booking: allocation
 * creates one booking per free working-day window in the slot's daily
 * breakdown (mirroring the backend allocate-daily endpoint). Slots without a
 * breakdown fall back to a single booking.
 */
export function countProposedBookings(slots: ProposedBookingSlot[]): number {
  return slots.reduce((sum, slot) => sum + (slot.dailyBreakdown?.length || 1), 0);
}

/**
 * Normalised per-month hint map: a whole-hour even spread of `totalHours`
 * across `months` with the remainder front-loaded ("minimum 1 hour a month,
 * spread down, remaining months 0").
 *
 * Incoming demand-line hints can carry a degenerate curve (e.g. 0h for early
 * months and the full amount in the last month, produced by EBProjects'
 * rounding), which would make a plan book nothing early and dump everything
 * into the final month. Deriving the spread purely from the total and the month
 * window avoids that.
 *
 * By construction the values sum to exactly `totalHours` (to 0.1h): the whole
 * hours are spread base * n + remainder = floor(total), and any fractional
 * part (e.g. the 0.5 of a 29.5h demand) is added to the first month so a
 * partially-filled demand is planned for its exact outstanding hours rather
 * than a rounded-up whole hour. When floor(total) < n, base is 0 so the first
 * floor(total) months get exactly 1h and the rest get 0h.
 */
export function computeEvenMonthlyHints(totalHours: number, months: string[]): Record<string, number> {
  const hints: Record<string, number> = {};
  const n = months.length;
  if (n === 0) return hints;
  const total = roundHours(totalHours);
  const whole = Math.floor(total);
  const fraction = roundHours(total - whole);
  const base = Math.floor(whole / n);
  const remainder = whole - base * n;
  for (let i = 0; i < n; i++) {
    hints[months[i]] = base + (i < remainder ? 1 : 0);
  }
  if (fraction > 0) {
    hints[months[0]] = roundHours(hints[months[0]] + fraction);
  }
  return hints;
}

/**
 * The month keys (`YYYY-MM`) a request's plan spans: from its requested start
 * to its requested end (defaulting to +2 months when no end is given), extended
 * to cover any months referenced by `monthlyHourHints`. Shared by the planner
 * and the request summary UI so both agree on the month window.
 *
 * Months before the first hint month are excluded: a partially-filled (or
 * budget-shifted) demand omits months with nothing left to allocate, so those
 * leading months would otherwise appear in the window pinned to 0h.
 */
export function monthsForRequest(request: ResourceRequest): string[] {
  let requestStart = parseRequestDate(request.requestedStartDate);
  let requestEnd = request.requestedEndDate
    ? parseRequestDate(request.requestedEndDate)
    : (() => {
        const d = new Date(requestStart);
        d.setMonth(d.getMonth() + 2);
        d.setDate(endOfMonth(ym(d)).getDate());
        return d;
      })();

  if (request.monthlyHourHints) {
    const hintMonths = Object.keys(request.monthlyHourHints).sort();
    if (hintMonths.length > 0) {
      const lastHintEnd = endOfMonth(hintMonths[hintMonths.length - 1]);
      if (lastHintEnd > requestEnd) requestEnd = lastHintEnd;
      const firstHintStart = startOfMonth(hintMonths[0]);
      if (firstHintStart > requestStart) requestStart = firstHintStart;
    }
  }

  return monthsInRange(requestStart, requestEnd);
}

/**
 * Normalised per-month spread for a request: the even whole-hour distribution
 * of its total hours across its month window (see computeEvenMonthlyHints).
 * This is what the plan actually books, so the UI should display this rather
 * than the raw `request.monthlyHourHints` (which can be degenerate).
 */
export function computeRequestMonthlyHints(request: ResourceRequest): Record<string, number> {
  return computeNormalisedHints(request, monthsForRequest(request));
}

/**
 * Per-month spread of a request's outstanding hours, restricted to the months
 * its `monthlyHourHints` still ask for.
 *
 * The demand's own monthly curve is authoritative: it is rescaled to the hours
 * that are still outstanding (see `scaleHintsToTotal`) so a demand asking for
 * 7.5h in November is planned for 7.5h, not an averaged 6h. Only a curve with
 * no usable shape falls back to an even spread.
 *
 * Months absent from the hints have nothing left to allocate (a partially
 * filled demand omits fully booked months) so they are pinned to 0 rather than
 * left undefined — the strategies treat a missing hint as "spread freely into
 * this month".
 */
function computeNormalisedHints(request: ResourceRequest, months: string[]): Record<string, number> {
  const demandHints = request.monthlyHourHints ?? {};
  const hintMonths = Object.keys(demandHints);
  const wanted = hintMonths.length > 0
    ? months.filter(m => hintMonths.includes(m))
    : months;
  const target = wanted.length > 0 ? wanted : months;
  const spread = scaleHintsToTotal(demandHints, target, request.totalHoursRequested)
    ?? computeEvenMonthlyHints(request.totalHoursRequested, target);
  return months.reduce<Record<string, number>>((acc, m) => {
    acc[m] = spread[m] ?? 0;
    return acc;
  }, {});
}

/**
 * Rescale the demand's own monthly curve onto `totalHours`, preserving its
 * shape to 0.1h and summing to exactly `totalHours` (the last month takes the
 * rounding remainder). When the curve already sums to the total this returns it
 * unchanged.
 *
 * Returns null when the curve carries no usable shape — no hours at all, or
 * every hour dumped into the final month while the earlier ones are 0 (a
 * degenerate curve produced by EBProjects' rounding). Honouring that shape
 * would make the plan book nothing until the last month, so the caller falls
 * back to an even spread instead.
 */
function scaleHintsToTotal(
  hints: Record<string, number>,
  months: string[],
  totalHours: number,
): Record<string, number> | null {
  if (months.length === 0) return null;
  const values = months.map(m => Math.max(0, hints[m] ?? 0));
  const sum = values.reduce((a, b) => a + b, 0);
  if (sum <= 0) return null;

  const monthsWithHours = values.filter(v => v > 0).length;
  const onlyLastMonth = monthsWithHours === 1 && values[values.length - 1] > 0;
  if (months.length > 1 && onlyLastMonth) return null;

  const total = roundHours(totalHours);
  const scaled: Record<string, number> = {};
  let allocated = 0;
  months.forEach((month, i) => {
    if (i === months.length - 1) {
      scaled[month] = Math.max(0, roundHours(total - allocated));
      return;
    }
    const share = Math.min(roundHours(total * (values[i] / sum)), roundHours(total - allocated));
    scaled[month] = Math.max(0, share);
    allocated = roundHours(allocated + scaled[month]);
  });
  return scaled;
}

// ── Main planning function ─────────────────────────────────────────────────────

export interface PlannerInput {
  request: ResourceRequest;
  resources: Resource[];
  existingBookings: Booking[];
  globalWorkingProfile: WorkingHoursProfile;
  businessHoursStart: number;
  businessHoursEnd: number;
  /** Absences taking hours — a whole day, or one half of it — off a resource. */
  absences?: Absence[];
  /** Bank holidays taking whole days off every resource. */
  bankHolidays?: BankHoliday[];
  /**
   * A plan-availability read already started for this request, used instead of
   * reading again when its key still matches the request being planned.
   */
  prefetchedAvailability?: PrefetchedPlanAvailability;
}

/** A plan-availability read started ahead of planning, keyed by what it asked for. */
export interface PrefetchedPlanAvailability {
  key: string;
  /** A summary response when the key is a summary read's, the full one otherwise. */
  response: Promise<PlanAvailabilityResponse | PlanAvailabilitySummaryResponse>;
}

/** The inclusive window a request is planned over: its dates, widened to every hinted month. */
function planRequestWindow(request: ResourceRequest): { requestStart: Date; requestEnd: Date } {
  const requestStart = parseRequestDate(request.requestedStartDate);
  let requestEnd = request.requestedEndDate
    ? parseRequestDate(request.requestedEndDate)
    : (() => {
        const d = new Date(requestStart);
        d.setMonth(d.getMonth() + 2);
        d.setDate(endOfMonth(ym(d)).getDate());
        return d;
      })();

  // Extend end date to cover all months referenced by monthly hour hints
  if (request.monthlyHourHints) {
    const hintMonths = Object.keys(request.monthlyHourHints).sort();
    if (hintMonths.length > 0) {
      const lastHintEnd = endOfMonth(hintMonths[hintMonths.length - 1]);
      if (lastHintEnd > requestEnd) requestEnd = lastHintEnd;
    }
  }

  return { requestStart, requestEnd };
}

function planAvailabilityQuery(
  request: ResourceRequest,
  businessHoursStart: number,
  businessHoursEnd: number,
): PlanAvailabilityRequest {
  const { requestStart, requestEnd } = planRequestWindow(request);
  return {
    // Date-only: the window is a range of calendar days, and sending instants
    // would move both ends by the client's UTC offset.
    startDate: iso(requestStart),
    endDate: iso(requestEnd),
    totalHours: request.totalHoursRequested,
    role: request.role,
    roleCode: request.roleCode,
    skills: request.skills,
    location: request.location,
    businessHoursStart,
    businessHoursEnd,
  };
}

/**
 * Identifies the pool and window a plan-availability read covers. The requested
 * hours are left out: the endpoint's result does not depend on them, so a read
 * started before hand-made hours were folded into the request still applies.
 */
function planAvailabilityKey(query: PlanAvailabilityRequest): string {
  const key: unknown[] = [
    query.startDate,
    query.endDate,
    query.role ?? null,
    query.roleCode ?? null,
    query.skills ?? [],
    query.location ?? null,
    query.businessHoursStart ?? null,
    query.businessHoursEnd ?? null,
  ];
  if (query.detail) key.push(query.detail);
  return JSON.stringify(key);
}

/** The plan-availability read planning makes: the pool's totals only, when summaries are on. */
function planAvailabilityReadQuery(
  request: ResourceRequest,
  businessHoursStart: number,
  businessHoursEnd: number,
): PlanAvailabilityRequest {
  const query = planAvailabilityQuery(request, businessHoursStart, businessHoursEnd);
  return featureFlags.isEnabled('planAvailabilitySummary') ? { ...query, detail: 'summary' } : query;
}

function readPlanAvailability(
  query: PlanAvailabilityRequest,
): Promise<PlanAvailabilityResponse | PlanAvailabilitySummaryResponse> {
  return query.detail === 'summary'
    ? resourceAvailabilityApi.planAvailabilitySummary(query)
    : resourceAvailabilityApi.planAvailability(query);
}

/**
 * The full plan-availability result — every free window and every day's
 * utilisation — for each of `resourceIds` in the pool `query` reads. `query` is
 * the plan's own read, so the pool and window match the totals it was ranked on;
 * a resource outside that pool has no result. Always read afresh, like the plan's
 * own read.
 */
export async function fetchPlanAvailabilityDetail(
  query: PlanAvailabilityRequest,
  resourceIds: number[],
): Promise<ResourcePlanResult[]> {
  const ids = [...new Set(resourceIds)];
  if (ids.length === 0) return [];
  const context: PlanAvailabilityRequest = { ...query };
  delete context.detail;
  const response = await resourceAvailabilityApi.planAvailabilityDetail({ ...context, resourceIds: ids });
  return response.resources;
}

/**
 * Identifies one resource's full plan-availability result for the pool and window
 * `query` reads, whichever detail level the read itself asked for.
 */
export function planDetailKey(query: PlanAvailabilityRequest, resourceId: string): string {
  const context: PlanAvailabilityRequest = { ...query };
  delete context.detail;
  return `${planAvailabilityKey(context)}|${resourceId}`;
}

/** Key of the plan-availability read `generatePlanScenarios` would make for the request. */
export function planAvailabilityKeyFor(
  request: ResourceRequest,
  businessHoursStart: number,
  businessHoursEnd: number,
): string {
  return planAvailabilityKey(planAvailabilityReadQuery(request, businessHoursStart, businessHoursEnd));
}

/**
 * Starts the request's plan-availability read so it can run alongside the other
 * reads planning waits on. A failed read is left for `generatePlanScenarios` to
 * handle, which falls back to local computation exactly as for its own read.
 */
export function prefetchPlanAvailability(
  request: ResourceRequest,
  businessHoursStart: number,
  businessHoursEnd: number,
): PrefetchedPlanAvailability {
  const query = planAvailabilityReadQuery(request, businessHoursStart, businessHoursEnd);
  const response = (async () => readPlanAvailability(query))();
  // Observed here so a read nobody ends up awaiting is not an unhandled rejection.
  response.catch(() => undefined);
  return { key: planAvailabilityKey(query), response };
}

/** A resource's locally known ranking keys, from the recommendation engine. */
interface PlanRankMatch {
  softSignalsMet: number;
  skillsCoveragePct: number;
  requiredSkillsCoveragePct: number;
  preferredSkillsCoveragePct: number;
  utilisationHeadroom: number;
  roleMet: boolean;
}

interface PlanRankOptions {
  roleFirst: boolean;
  hasRequiredSkills: boolean;
  hasSkillsRequirement: boolean;
}

/**
 * Orders two resources for the shortlist by skills coverage (only when skills
 * are requested) then absolute free capacity (most available hours first),
 * optionally preferring role-matched resources first.
 *
 * Skills coverage is required-first: a resource covering fewer of the required
 * skills sinks towards the bottom of the shortlist (covering none of them is
 * already out of the pool). Preferred skills only break ties between resources
 * with equal required coverage.
 */
function compareRankKeys(
  ma: PlanRankMatch | undefined,
  mb: PlanRankMatch | undefined,
  aHours: number,
  bHours: number,
  { roleFirst, hasRequiredSkills, hasSkillsRequirement }: PlanRankOptions,
): number {
  if (roleFirst) {
    // Role-matched resources always rank above unmatched ones.
    const roleDiff = Number(mb?.roleMet ?? false) - Number(ma?.roleMet ?? false);
    if (roleDiff !== 0) return roleDiff;
  }
  // Skills next — only when the request actually specifies skills.
  if (hasRequiredSkills) {
    const requiredDiff =
      (mb?.requiredSkillsCoveragePct ?? 0) - (ma?.requiredSkillsCoveragePct ?? 0);
    if (requiredDiff !== 0) return requiredDiff;
    const preferredDiff =
      (mb?.preferredSkillsCoveragePct ?? 0) - (ma?.preferredSkillsCoveragePct ?? 0);
    if (preferredDiff !== 0) return preferredDiff;
  } else if (hasSkillsRequirement) {
    const skillDiff = (mb?.skillsCoveragePct ?? 0) - (ma?.skillsCoveragePct ?? 0);
    if (skillDiff !== 0) return skillDiff;
  }
  // Capacity — absolute free hours in the window, most first. Utilisation
  // headroom is a percentage, so an idle part-timer with a couple of free
  // hours a day outranked a full-time resource with far more real capacity;
  // it only breaks ties between equal free capacity now.
  const hoursDiff = bHours - aHours;
  if (hoursDiff !== 0) return hoursDiff;
  return (mb?.utilisationHeadroom ?? 0) - (ma?.utilisationHeadroom ?? 0);
}

const ROLE_NEAR_HARD_MIN_MATCHES = 3;

/** Resources read in full per plan-availability detail read. */
const PLAN_DETAIL_BATCH_SIZE = 12;

/** The bound given a resource whose totals do not say how its windows round. */
const UNBOUNDED_PLAN_HOURS = 1e9;

/**
 * The most free hours `withMeasuredCapacity` can leave a resource with, from its
 * summary totals alone.
 *
 * Measuring caps windows and splits them around absences, which only takes
 * hours away — except that a part it keeps is rounded to a tenth of an hour, so
 * a part not already a whole number of six minutes can round up by 0.05h. A
 * part can only be uneven on a day whose free windows or absence boundaries are,
 * so the slack is counted for those days only: every part such a day can split
 * into, plus the one a capacity cap can shorten.
 */
export function measuredHoursUpperBound(
  row: ResourcePlanSummary,
  unavailableDaysByResourceId: Record<string, UnavailableDay[]>,
  blockedIntervalsFor: (resourceId: string, date: string) => DayInterval[],
): number {
  if (!(row.totalAvailableHours > 0)) return 0;
  if (row.unevenDays == null || row.maxDailySlots == null) return UNBOUNDED_PLAN_HOURS;

  const resourceId = String(row.resourceId);
  const seen = new Set<string>();
  let mostBlocked = 0;
  let unevenParts = 0;
  for (const unavailable of unavailableDaysByResourceId[resourceId] ?? []) {
    const date = unavailable.date.slice(0, 10);
    if (seen.has(date)) continue;
    seen.add(date);
    const blocked = blockedIntervalsFor(resourceId, date);
    if (blocked.length === 0) continue;
    mostBlocked = Math.max(mostBlocked, blocked.length);
    if (blocked.some(b => b.startMinutes % 6 !== 0 || b.endMinutes % 6 !== 0)) {
      unevenParts += row.maxDailySlots * (blocked.length + 1) + 1;
    }
  }
  unevenParts += row.unevenDays * (row.maxDailySlots * (mostBlocked + 1) + 1);

  return roundHours(row.totalAvailableHours + 0.05 * unevenParts + 1e-6);
}

function planCoverageOf(
  match: PlanRankMatch | undefined,
  key: 'requiredSkillsCoveragePct' | 'preferredSkillsCoveragePct',
): number {
  return match?.[key] ?? (key === 'requiredSkillsCoveragePct' ? 100 : 0);
}

/**
 * The shortlist the full read's path in `generatePlanScenarios` ranks — the same
 * skills top-up, role near-hard filter and ranking — from the pool's totals,
 * measuring only the resources each decision can depend on.
 *
 * Each decision is taken in the order the full path takes it, on exact measured
 * hours: who survives measuring, whether the required-skill holders' hours cover
 * the request, which preferred-skill holders top them up, how many role matches
 * survive, and the best three of the ranked pool. Candidates are identified by
 * their position in the list the full path would rank, so ties break the same way
 * its stable sort breaks them.
 */
async function shortlistFromSummary({
  rows, pool, upperBoundOf, matchById, hasRoleRequirement, hasRequiredSkills,
  hasSkillsRequirement, hasPreferredSkills, totalHours, request,
}: {
  rows: ResourcePlanSummary[];
  pool: MeasuredPool<ResourcePlanResult>;
  upperBoundOf: (row: ResourcePlanSummary) => number;
  matchById: Map<string, PlanRankMatch>;
  hasRoleRequirement: boolean;
  hasRequiredSkills: boolean;
  hasSkillsRequirement: boolean;
  hasPreferredSkills: boolean;
  totalHours: number;
  request: ResourceRequest;
}): Promise<{ ranked: ResourcePlanResult[]; roleFallback: boolean }> {
  const hoursOf = (measured: ResourcePlanResult) => measured.totalAvailableHours;
  const matchOf = (id: string) => matchById.get(id);

  // A resource with no free hours at all is dropped by measuring, unmeasured.
  const candidates: ShortlistCandidate[] = rows
    .map((row, index) => ({ id: String(row.resourceId), index, upperBound: upperBoundOf(row) }))
    .filter(candidate => candidate.upperBound > 0);

  let ranked: ShortlistCandidate[] = candidates;
  if (hasRequiredSkills) {
    const qualified = candidates.filter(
      c => planCoverageOf(matchOf(c.id), 'requiredSkillsCoveragePct') > 0,
    );
    const reserve = hasPreferredSkills
      ? candidates.filter(c =>
        planCoverageOf(matchOf(c.id), 'requiredSkillsCoveragePct') === 0
        && planCoverageOf(matchOf(c.id), 'preferredSkillsCoveragePct') > 0)
      : [];

    const topUp: ShortlistCandidate[] = [];
    if (reserve.length > 0) {
      const pooled = await pooledHoursUpTo(pool, qualified, totalHours, hoursOf);
      if (pooled !== 'enough') {
        const byPreference: RankComparator = (a, b) =>
          (planCoverageOf(matchOf(b.id), 'preferredSkillsCoveragePct')
            - planCoverageOf(matchOf(a.id), 'preferredSkillsCoveragePct'))
          || (b.hours - a.hours)
          || (a.index - b.index);
        const reserveOrder = orderedTaker(pool, reserve, byPreference, hoursOf);
        let pooledHours = pooled;
        while (pooledHours < totalHours) {
          const next = await reserveOrder.next();
          if (!next) break;
          topUp.push({
            id: String(next.resourceId),
            index: rows.length + topUp.length,
            upperBound: next.totalAvailableHours,
          });
          pooledHours += next.totalAvailableHours;
        }
      }
    }
    ranked = [...qualified, ...topUp];
  }

  const rankBy = (roleFirst: boolean): RankComparator => (a, b) =>
    compareRankKeys(
      matchOf(a.id), matchOf(b.id), a.hours, b.hours,
      { roleFirst, hasRequiredSkills, hasSkillsRequirement },
    ) || (a.index - b.index);
  const bestThree = (list: ShortlistCandidate[], roleFirst: boolean) =>
    takeRanked(orderedTaker(pool, list, rankBy(roleFirst), hoursOf), 3);

  let shortlist: ResourcePlanResult[];
  let roleFallback = false;
  if (hasRoleRequirement) {
    // Only whether none, fewer than three or three or more role matches survive
    // decides the path, and the best three of them are that count.
    const roleMatched = await bestThree(ranked.filter(c => matchOf(c.id)?.roleMet === true), true);
    console.warn(
      `[planAvailability] Role near-hard filter: ${roleMatched.length >= ROLE_NEAR_HARD_MIN_MATCHES ? `${ROLE_NEAR_HARD_MIN_MATCHES}+` : roleMatched.length}/${ranked.length} candidate resources matched requested role "${request.role}" (code ${request.roleCode ?? 'n/a'})`,
    );
    roleFallback = roleMatched.length === 0;
    if (roleMatched.length >= ROLE_NEAR_HARD_MIN_MATCHES || (roleMatched.length > 0 && !hasSkillsRequirement)) {
      shortlist = roleMatched;
    } else if (roleMatched.length > 0) {
      shortlist = await bestThree(ranked, true);
    } else {
      shortlist = await bestThree(ranked, false);
    }
  } else {
    shortlist = await bestThree(ranked, false);
  }

  for (const r of shortlist) {
    r.matchScore = matchOf(String(r.resourceId))?.softSignalsMet ?? 0;
  }
  return { ranked: shortlist, roleFallback };
}

export async function generatePlanScenarios(input: PlannerInput): Promise<PlanScenario[]> {
  const { request, businessHoursStart, businessHoursEnd } = input;

  const { requestStart, requestEnd } = planRequestWindow(request);

  // Same month window as monthsForRequest (kept inline because requestEnd is
  // also needed below for slot filtering and must stay the true requested end).
  const months = monthsInRange(requestStart, requestEnd);
  const totalHours = request.totalHoursRequested;

  // Normalised per-month hint map, used everywhere the strategies below would
  // otherwise read `request.monthlyHourHints` verbatim. Derived purely from the
  // total and the month window so a degenerate incoming curve can't dump every
  // hour into the final month (see computeEvenMonthlyHints).
  const effectiveHints = computeNormalisedHints(request, months);

  // ── Try backend; fall back to local mock computation ────────────────────
  let planResult: ResourcePlanResult[];
  let spreadPlanResult: ResourcePlanResult[] | undefined;
  let usedBackendData = false;
  // Set when the role near-hard filter finds no role-matched resource and the
  // planner falls back to the full eligible pool (PRD §7.2). Threaded onto
  // every generated slot so the UI can warn planners.
  let roleFallbackWarning = false;
  // Resource ids that match the requested role. Strategies use this to give
  // role-matched resources first priority when selecting who fills each month,
  // ahead of more-available non-role resources — a role match is only skipped
  // for a month when it has no free time that month at all.
  const roleMatchedIds = new Set<string>();
  // Authoritative utilisation % per resource from the backend. Empty on the local
  // fallback path (no backend), where the client still derives utilisation from the
  // mock booking list.
  const utilisationByResourceId: Record<string, number> = {};
  // Per-day capacity/booked hours behind those percentages, so each proposed slot can
  // be scored on its own dates rather than on the whole request window.
  const utilisationDaysByResourceId: Record<string, DailyUtilisationSlot[]> = {};
  // The read a summary plan was ranked on, so resources it did not read in full
  // can be read later for the same pool and window.
  let planAvailabilityContext: PlanAvailabilityRequest | undefined;
  const utilisationMonthsByResourceId: Record<string, MonthlyUtilisationSlot[]> = {};

  // Capacity as the drawer's validator measures it, so every proposed slot is
  // sized to the hours `validatePlanSlots` will allow rather than to the backend
  // availability breakdown alone — the two disagree by a few hours whenever a
  // resource's working profile and the breakdown round differently, which used
  // to hard-block plans nobody had edited.
  //
  // The inputs are deliberately the drawer's: the same custom-or-global working
  // profile, the same client-side bookings, and the same backend per-day booked
  // hours it passes as `reportedBookedDaysByResourceId` (read when a resource is
  // first measured, which is after the response has filled them in).
  //
  // Absences and bank holidays are measured with it: the availability query does
  // not always know about them, so a day a resource is on leave came back free
  // and was proposed in full. The days they take are the drawer's own
  // `unavailableDaysByResourceId`, so what the plan proposes and what the drawer
  // allows are one figure.
  const resourceById = new Map(input.resources.map(resource => [String(resource.id), resource]));
  const unavailableDaysByResourceId = unavailableDaysForResources({
    resourceIds: [...resourceById.keys()],
    startDate: requestStart,
    endDate: requestEnd,
    bankHolidays: input.bankHolidays,
    absences: input.absences,
    workingProfileFor: resourceId => effectiveWorkingProfile(
      resourceById.get(resourceId),
      input.globalWorkingProfile,
    ),
    businessHoursStart,
    businessHoursEnd,
  });

  const capacityFor = capacityLookup({
    existingBookings: input.existingBookings,
    resources: input.resources,
    globalWorkingProfile: input.globalWorkingProfile,
    reportedBookedDaysByResourceId: utilisationDaysByResourceId,
    unavailableDaysByResourceId,
  });

  /** The halves of a day an absence takes, so no window is proposed inside one. */
  const blockedIntervalsFor: BlockedIntervalLookup = (resourceId, date) => {
    const day = date.slice(0, 10);
    const windows = (unavailableDaysByResourceId[resourceId] ?? [])
      .filter((unavailable: UnavailableDay) => unavailable.date.slice(0, 10) === day)
      .flatMap((unavailable: UnavailableDay) => unavailable.lostWindows ?? []);
    return dayIntervalsFromWindows(windows);
  };

  try {
    const reco = computeRecommendations({
      startDate: requestStart,
      endDate: requestEnd,
      requiredRole: request.role,
      requiredRoleCode: request.roleCode,
      requiredSkills: request.skills,
      requestedSkills: request.skillsDetailed,
      allResources: input.resources,
      existingBookings: input.existingBookings,
    });
    const matchById = new Map<string, {
      softSignalsMet: number;
      skillsCoveragePct: number;
      requiredSkillsCoveragePct: number;
      preferredSkillsCoveragePct: number;
      utilisationHeadroom: number;
      roleMet: boolean;
    }>();
    for (const m of [...reco.cleanMatches, ...reco.conflictMatches]) {
      const roleMet = m.signals.find(s => s.key === 'role')?.met ?? false;
      matchById.set(String(m.resource.id), {
        softSignalsMet: m.softSignalsMet,
        skillsCoveragePct: m.skillsCoveragePct,
        // Coverage split by strength of the ask. Fall back to the combined figure
        // so a caller that only reports overall coverage still ranks as before.
        requiredSkillsCoveragePct: m.requiredSkillsCoveragePct ?? m.skillsCoveragePct,
        preferredSkillsCoveragePct: m.preferredSkillsCoveragePct ?? 0,
        utilisationHeadroom: m.utilisationHeadroom,
        roleMet,
      });
      if (roleMet) roleMatchedIds.add(String(m.resource.id));
    }

    const { required: requiredSkillNames, preferred: preferredSkillNames } =
      splitRequestedSkills(request.skills, request.skillsDetailed);
    const hasSkillsRequirement = request.skills.length > 0;
    // Required skills are ranked on explicitly; a request carrying only preferred
    // skills stays a soft preference and never reorders past utilisation.
    const hasRequiredSkills = requiredSkillNames.length > 0;

    const hasRoleRequirement =
      (typeof request.role === 'string' && request.role.trim() !== '') || request.roleCode != null;

    const availabilityQuery = planAvailabilityReadQuery(request, businessHoursStart, businessHoursEnd);
    const prefetched = input.prefetchedAvailability;
    const response = prefetched && prefetched.key === planAvailabilityKey(availabilityQuery)
      ? await prefetched.response
      : await readPlanAvailability(availabilityQuery);

    if (availabilityQuery.detail === 'summary') {
      // The read carried the pool's totals only. Ranking needs nothing more for
      // the pool; only the resources the shortlist can depend on are read in
      // full and measured, exactly as the full read's rows are below.
      const summary = response as PlanAvailabilitySummaryResponse;
      usedBackendData = true;
      planAvailabilityContext = availabilityQuery;
      for (const r of summary.resources) {
        if (typeof r.utilisationPercent === 'number') {
          utilisationByResourceId[String(r.resourceId)] = r.utilisationPercent;
        }
        if (r.monthlyUtilisation && r.monthlyUtilisation.length > 0) {
          utilisationMonthsByResourceId[String(r.resourceId)] = r.monthlyUtilisation;
        }
      }

      const measuredPool = new MeasuredPool<ResourcePlanResult>(async ids => {
        const detailed = await fetchPlanAvailabilityDetail(availabilityQuery, ids.map(Number));
        for (const r of detailed) {
          if (r.dailyUtilisation && r.dailyUtilisation.length > 0) {
            utilisationDaysByResourceId[String(r.resourceId)] = r.dailyUtilisation;
          }
        }
        const measured = withMeasuredCapacity(
          detailed, capacityFor, blockedIntervalsFor, requestStart, requestEnd,
        );
        return new Map(measured.map(r => [String(r.resourceId), r]));
      }, PLAN_DETAIL_BATCH_SIZE);

      const shortlist = await shortlistFromSummary({
        rows: summary.resources,
        pool: measuredPool,
        upperBoundOf: row => measuredHoursUpperBound(row, unavailableDaysByResourceId, blockedIntervalsFor),
        matchById,
        hasRoleRequirement,
        hasRequiredSkills,
        hasSkillsRequirement,
        hasPreferredSkills: preferredSkillNames.length > 0,
        totalHours,
        request,
      });
      roleFallbackWarning = shortlist.roleFallback;
      planResult = shortlist.ranked;
      spreadPlanResult = planResult.slice(0, 3);
    } else {
      const fullResponse = response as PlanAvailabilityResponse;
      planResult = fullResponse.resources;
      // `response.roleFallback` only says the backend widened the pool it returned
      // (it does so whenever fewer than 3 of its own role matches exist). Whether
      // the plan actually had to leave the role is decided by the frontend's own
      // role filter below, so the warning is set from that — reporting "no
      // resources match the requested role" while proposing someone holding it
      // was the banner planners saw.
      usedBackendData = true;

      // Capture utilisation for every resource the backend scored (before the pools
      // are sliced) so both the suggested plan and the edit-mode resource picker can
      // show real pre-existing utilisation.
      for (const r of fullResponse.resources) {
        if (typeof r.utilisationPercent === 'number') {
          utilisationByResourceId[String(r.resourceId)] = r.utilisationPercent;
        }
        if (r.dailyUtilisation && r.dailyUtilisation.length > 0) {
          utilisationDaysByResourceId[String(r.resourceId)] = r.dailyUtilisation;
        }
      }

      // Only resources that can really take work stay in the pool, and only the
      // days they can take it on stay in their breakdown. Filtering before the
      // role/skills ranking below is what makes a fully booked resource give way
      // to the next resource of the same role, and then to a skills match.
      planResult = withMeasuredCapacity(
        planResult, capacityFor, blockedIntervalsFor, requestStart, requestEnd,
      );

      // Required skills are an OR and a hard condition: holding any one of them
      // is enough, holding none of them is never proposed — not even for a
      // role match, which is the case that kept surfacing 0-of-3 resources.
      //
      // Holders of a PREFERRED skill only are a reserve: they join the pool,
      // best preferred coverage first, while the required-skill holders cannot
      // between them cover the requested hours — so a single required-skill
      // resource with 40h free no longer caps a 120h request at one suggestion.
      if (hasRequiredSkills) {
        const coverageOf = (r: ResourcePlanResult, key: 'requiredSkillsCoveragePct' | 'preferredSkillsCoveragePct') =>
          matchById.get(String(r.resourceId))?.[key] ?? (key === 'requiredSkillsCoveragePct' ? 100 : 0);
        const qualified = planResult.filter(r => coverageOf(r, 'requiredSkillsCoveragePct') > 0);
        const reserve = planResult
          .filter(r => preferredSkillNames.length > 0
            && coverageOf(r, 'requiredSkillsCoveragePct') === 0
            && coverageOf(r, 'preferredSkillsCoveragePct') > 0)
          .sort((a, b) =>
            (coverageOf(b, 'preferredSkillsCoveragePct') - coverageOf(a, 'preferredSkillsCoveragePct'))
            || (b.totalAvailableHours - a.totalAvailableHours));

        let pooledHours = qualified.reduce((sum, r) => sum + r.totalAvailableHours, 0);
        const topUp: ResourcePlanResult[] = [];
        for (const r of reserve) {
          if (pooledHours >= totalHours) break;
          topUp.push(r);
          pooledHours += r.totalAvailableHours;
        }

        planResult = [...qualified, ...topUp];
        if (planResult.length === 0) return [];
      }

      // The backend returns the role-matched active pool (when 3+ match) or the
      // full pool otherwise. Rank it client-side (PRD §6.3), mapping each backend
      // result to its frontend Resource by id.
      for (const r of planResult) {
        r.matchScore = matchById.get(String(r.resourceId))?.softSignalsMet ?? 0;
      }

      // Rank by skills coverage (only when skills are requested) then absolute
      // free capacity (most available hours first), optionally preferring
      // role-matched resources first.
      //
      // Skills coverage is required-first: a resource covering fewer of the
      // required skills sinks towards the bottom of the shortlist (covering none
      // of them is already out of the pool). Preferred skills only break ties
      // between resources with equal required coverage.
      const rankResources = (
        list: ResourcePlanResult[],
        { roleFirst }: { roleFirst: boolean },
      ): ResourcePlanResult[] =>
        [...list].sort((a, b) => compareRankKeys(
          matchById.get(String(a.resourceId)),
          matchById.get(String(b.resourceId)),
          a.totalAvailableHours,
          b.totalAvailableHours,
          { roleFirst, hasRequiredSkills, hasSkillsRequirement },
        ));

      // ── Role "near-hard" filter (PRD §7.2) ────────────────────────────────
      // Behaviour by number of resources matching the requested role:
      //  - 3 or more  → role is a hard constraint; every strategy uses ONLY the
      //                 role-matched resources.
      //  - 1 or 2     → depends on whether skills were requested:
      //                 · no skills → role is the only stated requirement, so it
      //                   stays a hard constraint however few resources match; the
      //                   plan never offers someone outside the role.
      //                 · skills requested → splitting the load with non-role
      //                   people is acceptable provided they cover the skills, so
      //                   the matched resources are surfaced FIRST (role ranked
      //                   first) and the pool is supplemented with others ranked on
      //                   skills + utilisation. No fallback banner — real role
      //                   matches DO exist and are shown.
      //  - 0          → nothing matches the role; drop the signal, rank the full
      //                 pool on skills + utilisation and surface a roleFallback
      //                 warning on generated slots.
      // (This is intentionally NOT applied in recommendationEngine.ts, where the
      // suggestions dialog / unallocated flow keeps role as a soft signal.)
      if (hasRoleRequirement) {
        const roleMatched = planResult.filter(
          r => matchById.get(String(r.resourceId))?.roleMet === true,
        );
        // Diagnostic: surfaces how many resources matched the requested role vs
        // the full eligible pool, so a silent full-pool fallback (or a role
        // string / company-linkage misconfiguration) is diagnosable in the field.
        console.warn(
          `[planAvailability] Role near-hard filter: ${roleMatched.length}/${planResult.length} resources matched requested role "${request.role}" (code ${request.roleCode ?? 'n/a'})`,
        );
        // A resource holding the role is proposed unless none is left at all.
        roleFallbackWarning = roleMatched.length === 0;
        if (roleMatched.length >= ROLE_NEAR_HARD_MIN_MATCHES || (roleMatched.length > 0 && !hasSkillsRequirement)) {
          planResult = rankResources(roleMatched, { roleFirst: true });
          spreadPlanResult = planResult.slice(0, 3);
        } else if (roleMatched.length > 0) {
          // Some (but < 3) role matches — keep them first, supplement with others.
          planResult = rankResources(planResult, { roleFirst: true });
          spreadPlanResult = planResult.slice(0, 3);
        } else {
          // No role matches at all — drop the role signal and rank the full pool
          // on skills + utilisation only.
          planResult = rankResources(planResult, { roleFirst: false });
          spreadPlanResult = planResult.slice(0, 3);
        }
      } else {
        planResult = rankResources(planResult, { roleFirst: false });
        spreadPlanResult = planResult.slice(0, 3);
      }

      // ── Top-3 shortlist ───────────────────────────────────────────────────
      // Single Resource and Fastest Start keep only the best 3 role matches.
      // Spread the Load retained its separate three-resource shortlist above.
      // In a zero-match fallback both shortlists use the best available pool.
      planResult = planResult.slice(0, 3);
    }
  } catch {
    // Backend unavailable — compute availability from local mock data
    console.info('[planAvailability] Backend unavailable — using local mock fallback');
    planResult = computeLocalPlanResult({
      request,
      resources: input.resources,
      existingBookings: input.existingBookings,
      globalWorkingProfile: input.globalWorkingProfile,
      businessHoursStart,
      businessHoursEnd,
      requestStart,
      requestEnd,
    });
    spreadPlanResult = planResult;
  }

  // ── Split daily slots into free time windows around existing bookings ────
  // Only for the LOCAL FALLBACK: computeLocalPlanResult returns one slot per
  // day (total available hours) without indicating where existing bookings
  // sit, so we re-split it here using input.existingBookings.
  //
  // The backend already returns per-gap slots (with explicit start/end times)
  // computed from authoritative DB bookings, so re-splitting it against the
  // client-side booking list — which can be incomplete in live mode — would
  // wrongly widen a partially-booked day back to the full window. Trust the
  // backend breakdown as-is when it was used.
  const DAY_KEYS_MAIN: (keyof WorkingHoursProfile)[] = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
  ];

  for (const r of (usedBackendData ? [] : planResult)) {
    const frontendResource = resourceById.get(String(r.resourceId));
    if (!frontendResource) continue;

    const profile = frontendResource.customWorkingProfile;
    if (!profile) continue;

    // Collect existing bookings for this resource
    const resBookings = input.existingBookings.filter(
      bk => String(bk.resourceId) === String(frontendResource.id) && consumesCapacity(bk),
    );

    const newBreakdown: DailyAvailabilitySlot[] = [];

    for (const slot of r.dailyBreakdown) {
      const dayOfWeek = parseSlotDate(slot.date).getDay();
      const dayKey = DAY_KEYS_MAIN[dayOfWeek];
      const contracted = (profile as unknown as Record<string, number>)[dayKey as string] ?? 0;
      if (contracted <= 0) continue;

      // Working window for this day
      const startHH = String(businessHoursStart).padStart(2, '0');
      const endHH = String(businessHoursEnd).padStart(2, '0');
      const windowStart = new Date(`${slot.date}T${startHH}:00:00Z`).getTime();
      const windowEnd = new Date(`${slot.date}T${endHH}:00:00Z`).getTime();

      // Find bookings that overlap this day's working window
      const dayBookings: { start: number; end: number }[] = [];
      for (const bk of resBookings) {
        try {
          const bkStart = new Date(bk.startTime).getTime();
          const bkEnd = new Date(bk.endTime).getTime();
          if (bkEnd > windowStart && bkStart < windowEnd) {
            dayBookings.push({
              start: Math.max(bkStart, windowStart),
              end: Math.min(bkEnd, windowEnd),
            });
          }
        } catch { /* skip malformed */ }
      }

      if (dayBookings.length === 0) {
        // No bookings — full contracted hours available as single window
        newBreakdown.push({
          date: slot.date,
          startTime: `${slot.date}T${startHH}:00:00Z`,
          endTime: `${slot.date}T${endHH}:00:00Z`,
          hours: contracted,
          availableHours: contracted,
        });
      } else {
        // Sort bookings by start time and compute free gaps
        dayBookings.sort((a, b) => a.start - b.start);

        // Merge overlapping bookings
        const merged: { start: number; end: number }[] = [];
        for (const bk of dayBookings) {
          if (merged.length > 0 && bk.start <= merged[merged.length - 1].end) {
            merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, bk.end);
          } else {
            merged.push({ ...bk });
          }
        }

        // Build free windows between bookings
        let cursor = windowStart;
        for (const bk of merged) {
          if (bk.start > cursor) {
            const gapHours = (bk.start - cursor) / 3600000;
            if (gapHours > 0) {
              newBreakdown.push({
                date: slot.date,
                startTime: new Date(cursor).toISOString(),
                endTime: new Date(bk.start).toISOString(),
                hours: Math.round(gapHours * 10) / 10,
                availableHours: Math.round(gapHours * 10) / 10,
              });
            }
          }
          cursor = Math.max(cursor, bk.end);
        }
        // Gap after last booking until end of working window
        if (cursor < windowEnd) {
          const gapHours = (windowEnd - cursor) / 3600000;
          if (gapHours > 0) {
            newBreakdown.push({
              date: slot.date,
              startTime: new Date(cursor).toISOString(),
              endTime: new Date(windowEnd).toISOString(),
              hours: Math.round(gapHours * 10) / 10,
              availableHours: Math.round(gapHours * 10) / 10,
            });
          }
        }
      }
    }

    r.dailyBreakdown = newBreakdown;
  }

  // The local fallback sizes days from the working profile and the mock bookings
  // alone, so it is measured here — after the re-split above, which would
  // otherwise widen a day back over an absence it had just been narrowed around.
  if (!usedBackendData) {
    planResult = withMeasuredCapacity(
      planResult, capacityFor, blockedIntervalsFor, requestStart, requestEnd,
    );
    spreadPlanResult = planResult;
  }

  if (planResult.length === 0) return [];
  const spreadResources = spreadPlanResult ?? planResult;

  // Build per-resource per-month availability for both strategy pools.
  type AvailMap = Record<number, Record<string, number>>;
  const availMap: AvailMap = {};
  type BreakdownCache = Record<number, Record<string, DailyAvailabilitySlot[]>>;
  const breakdownCache: BreakdownCache = {};

  const availabilityPlanResult = [
    ...new Map(
      [...planResult, ...spreadResources].map(resource => [resource.resourceId, resource]),
    ).values(),
  ];

  // Drop anything outside the requested window before bucketing so no path —
  // backend breakdown or local fallback re-split — can propose a day earlier
  // than the requested start (or later than the requested end).
  //
  // A partially-booked day stays in: the backend reports the hours it still has
  // free, and a slot may take those, so long as the drawer's per-day capacity
  // check finds room for what the slot allocates.
  for (const r of availabilityPlanResult) {
    r.dailyBreakdown = r.dailyBreakdown.filter(s => {
      const d = parseSlotDate(s.date);
      return d >= startOfDay(requestStart) && d <= startOfDay(requestEnd);
    });
  }

  for (const r of availabilityPlanResult) {
    availMap[r.resourceId] = {};
    breakdownCache[r.resourceId] = {};
    for (const monthKey of months) {
      const monthSlots = slotsForMonth(r.dailyBreakdown, monthKey, requestStart, requestEnd);
      breakdownCache[r.resourceId][monthKey] = monthSlots;
      availMap[r.resourceId][monthKey] = sumHours(monthSlots.map(s => s.availableHours));
    }
  }

  // Skills is only a criterion when the request asks for skills — otherwise the
  // plan is scored on utilisation + role alone and must not mention skills.
  const criteriaTotal = request.skills.length > 0 ? 3 : 2;

  // When nothing matches the requested role the plan falls back to whoever has
  // the most capacity, so a scenario must not claim a role match it doesn't have.
  const requestedRoleLabel = (request.role ?? '').trim();
  const requestStatedRole = requestedRoleLabel !== '' || request.roleCode != null;
  const matchesRequestedRole = (resource: ResourcePlanResult): boolean => {
    if (!requestStatedRole) return true;
    if (roleMatchedIds.has(String(resource.resourceId))) return true;
    return normaliseRole(resource.resourceRole) === normaliseRole(request.role);
  };
  const roleMismatchCon = (resourceName: string): string =>
    requestedRoleLabel !== ''
      ? `${resourceName} does not match the required ${requestedRoleLabel} role`
      : `${resourceName} does not match the requested role`;

  // ── Helper: build signal reason strings ──────────────────────────────────
  function matchReasons(softSignalsMet: number, resourceRole: string): string[] {
    const reasons: string[] = [];
    reasons.push(`${softSignalsMet} of ${criteriaTotal} criteria met`);
    if (resourceRole) reasons.push(`Role: ${resourceRole}`);
    if (request.skills.length > 0) reasons.push(`Skills: ${request.skills.slice(0, 3).join(', ')}`);
    return reasons;
  }

  // ── Strategy 1: Single Resource ──────────────────────────────────────────
  function buildSingleResourceScenario(): PlanScenario | null {
    // Every candidate is planned out, not just the first one that produces any
    // slots: a resource who can absorb the whole request wins, and failing that
    // the one who covers the most of it. Ranking order breaks ties, so role and
    // skills preferences still lead among equally capable resources.
    let best: { resource: ResourcePlanResult; slots: ProposedBookingSlot[]; covered: number } | null = null;

    for (const resource of planResult) {
      const rid = resource.resourceId;
      let remainingHours = totalHours;
      const slots: ProposedBookingSlot[] = [];

      for (const monthKey of months) {
        if (remainingHours <= 0) break;
        const avail = availMap[rid]?.[monthKey] ?? 0;
        if (avail <= 0) continue;

        const hintHours = effectiveHints[monthKey];
        // The month hint is a target, not a licence to overbook: it is capped to
        // what the resource actually has free this month, as the spread and
        // fastest-start strategies already do with their shares.
        const maxThisMonth = hintHours != null
          ? Math.min(hintHours, avail, remainingHours)
          : Math.min(avail, remainingHours);

        const serverSlots = breakdownCache[rid]?.[monthKey] ?? [];
        const distribution = distributeFromServerSlots(serverSlots, maxThisMonth, businessHoursStart, businessHoursEnd);
        const planned = distribution && finaliseDistribution(distribution, rid, capacityFor);

        if (planned) {
          const reasons = matchReasons(resource.matchScore, resource.resourceRole);
          reasons.push(...planned.dailyBreakdown);

          slots.push({
            id: slotId(),
            resourceId: String(rid),
            resourceName: resource.resourceName,
            resourceRole: resource.resourceRole,
            resourcePhotoUrl: undefined,
            softSignalsMet: resource.matchScore,
            month: monthLabel(monthKey),
            monthKey,
            startDate: planned.startDate,
            endDate: planned.endDate,
            hours: planned.hours,
            availableHours: Math.round(avail),
            matchReasons: reasons,
            dailyBreakdown: planned.dailyAllocations,
            roleFallback: roleFallbackWarning,
          });
          // Only the hours the slot really books are spent, so anything the
          // capacity clamp removed carries to the next month.
          remainingHours = roundHours(remainingHours - planned.hours);
        }
      }

      if (slots.length > 0) {
        const covered = roundHours(totalHours - remainingHours);
        if (best == null || covered > best.covered) {
          best = { resource, slots, covered };
        }
        // Nobody can do better than covering the whole request.
        if (covered >= totalHours) break;
      }
    }

    if (best == null) return null;

    const { resource, slots, covered } = best;
    const roleMatched = matchesRequestedRole(resource);
    return {
      id: 'sc-single-resource',
      strategy: 'single-resource',
      label: 'Single Resource',
      description: `All ${covered}h assigned to ${resource.resourceName} — simplest coordination`,
      icon: 'User',
      totalHours: covered,
      slots,
      coveragePercent: Math.round((covered / totalHours) * 100),
      resourceCount: 1,
      earliestStart: slots[0].startDate,
      latestEnd: slots[slots.length - 1].endDate,
      pros: [
        'Single point of contact — simplest communication',
        request.skills.length > 0
          ? `${resource.resourceName} is the best match for required skills`
          : roleMatched
            ? `${resource.resourceName} matches the required ${requestedRoleLabel} role`
            : `${resource.resourceName} has the most capacity for this work`,
        'Consistent quality — one person owns the deliverable end-to-end',
      ],
      cons: [
        ...(roleMatched ? [] : [roleMismatchCon(resource.resourceName)]),
        'Single point of failure if the resource becomes unavailable',
        'May overload the resource compared to spreading the work',
      ],
    };
  }

  // ── Strategy 2: Spread Load ──────────────────────────────────────────────
  function buildSpreadLoadScenario(): PlanScenario | null {
    const slots: ProposedBookingSlot[] = [];
    let remainingHours = totalHours;
    // Hours each resource already holds across the months planned so far. The
    // split levels these running totals rather than each month in isolation, so
    // a resource with no free time in an early month is caught up later instead
    // of finishing the plan with a fraction of the work.
    const assignedByResource = new Map<number, number>();

    for (const monthKey of months) {
      if (remainingHours <= 0) break;

      const hintHours = effectiveHints[monthKey];
      let monthBudget = hintHours ?? roundHours(remainingHours / Math.max(1, months.length - months.indexOf(monthKey)));
      monthBudget = Math.min(monthBudget, remainingHours);

      // Spread across the top 3 available resources proportionally to their
      // availability (PRD §7.3). Role-matched resources get first priority even
      // when they have less availability (only excluded when they have no free
      // time this month); the rest are ordered by availability desc.
      const resourcesWithAvail = spreadResources
        .map(r => ({ resource: r, avail: availMap[r.resourceId]?.[monthKey] ?? 0 }))
        .filter(x => x.avail > 0)
        .sort((a, b) => {
          const roleDiff =
            Number(roleMatchedIds.has(String(b.resource.resourceId))) -
            Number(roleMatchedIds.has(String(a.resource.resourceId)));
          if (roleDiff !== 0) return roleDiff;
          return b.avail - a.avail;
        })
        .slice(0, 3);

      // Recompute the total availability against only the capped list so
      // proportional shares are relative to the 3 chosen resources.
      const totalAvailThisMonth = resourcesWithAvail.reduce((s, x) => s + x.avail, 0);
      if (totalAvailThisMonth === 0) continue;

      // The month's budget is shared out as evenly as the resources' free time
      // allows, rather than in proportion to how much each one happens to have
      // free: with comparable availability everyone gets the same hours, and
      // only a resource that cannot absorb an equal share hands its surplus to
      // the others.
      const shares = splitEvenlyByAvailability(
        monthBudget,
        resourcesWithAvail.map(x => x.avail),
        resourcesWithAvail.map(x => assignedByResource.get(x.resource.resourceId) ?? 0),
      );

      // Hours a resource's share could not book (its days had less room than the
      // share) are offered to the resources planned after it in the same month
      // before they roll on to the next month.
      let carry = 0;

      for (let i = 0; i < resourcesWithAvail.length; i++) {
        if (monthBudget <= 0) break;
        const { resource, avail } = resourcesWithAvail[i];
        const rid = resource.resourceId;

        const assignedHours = Math.min(roundHours(shares[i] + carry), monthBudget);
        if (assignedHours <= 0) continue;

        const serverSlots = breakdownCache[rid]?.[monthKey] ?? [];
        const distribution = distributeFromServerSlots(serverSlots, assignedHours, businessHoursStart, businessHoursEnd);
        const planned = distribution && finaliseDistribution(distribution, rid, capacityFor);

        if (planned) {
          const reasons = matchReasons(resource.matchScore, resource.resourceRole);
          reasons.push(...planned.dailyBreakdown);

          slots.push({
            id: slotId(),
            resourceId: String(rid),
            resourceName: resource.resourceName,
            resourceRole: resource.resourceRole,
            resourcePhotoUrl: undefined,
            softSignalsMet: resource.matchScore,
            month: monthLabel(monthKey),
            monthKey,
            startDate: planned.startDate,
            endDate: planned.endDate,
            hours: planned.hours,
            availableHours: Math.round(avail),
            matchReasons: reasons,
            dailyBreakdown: planned.dailyAllocations,
            roleFallback: roleFallbackWarning,
          });
          // The clamped hours are what is spent, so hours it removed stay with
          // the month budget for the next resource (and the next month).
          monthBudget = roundHours(monthBudget - planned.hours);
          remainingHours = roundHours(remainingHours - planned.hours);
          carry = Math.max(0, roundHours(assignedHours - planned.hours));
          assignedByResource.set(rid, roundHours((assignedByResource.get(rid) ?? 0) + planned.hours));
        } else {
          carry = roundHours(assignedHours);
        }
      }
    }

    if (slots.length === 0) return null;

    const covered = roundHours(totalHours - remainingHours);
    const uniqueResources = new Set(slots.map(s => s.resourceId)).size;

    return {
      id: 'sc-spread-load',
      strategy: 'spread-load',
      label: 'Spread the Load',
      description: `Hours distributed across ${uniqueResources} ${uniqueResources === 1 ? 'resource' : 'resources'} to balance capacity`,
      icon: 'Users',
      totalHours: covered,
      slots,
      coveragePercent: Math.round((covered / totalHours) * 100),
      resourceCount: uniqueResources,
      earliestStart: slots[0].startDate,
      latestEnd: slots[slots.length - 1].endDate,
      pros: [
        uniqueResources === 1
          ? 'One resource covers the workload — no coordination overhead'
          : `${uniqueResources} resources share the workload — reduces individual pressure`,
        'Better risk coverage if one resource becomes unavailable',
        'Leverages the most available capacity across the team',
      ],
      cons: [
        'Requires coordination and knowledge sharing between resources',
        'Slightly higher management overhead',
      ],
    };
  }

  // ── Strategy 3: Fastest Start ────────────────────────────────────────────
  function buildFastestStartScenario(): PlanScenario | null {
    const slots: ProposedBookingSlot[] = [];
    let remainingHours = totalHours;

    for (const monthKey of months) {
      if (remainingHours <= 0) break;

      // Role-matched resources come first (only skipped when they have no free
      // time this month); within each group, order by available hours desc.
      const byAvailability = planResult
        .map(r => ({ resource: r, avail: availMap[r.resourceId]?.[monthKey] ?? 0 }))
        .filter(x => x.avail > 0)
        .sort((a, b) => {
          const roleDiff =
            Number(roleMatchedIds.has(String(b.resource.resourceId))) -
            Number(roleMatchedIds.has(String(a.resource.resourceId)));
          if (roleDiff !== 0) return roleDiff;
          return b.avail - a.avail;
        });

      if (byAvailability.length === 0) continue;

      const hintHours = effectiveHints[monthKey];
      let monthBudget = hintHours ?? roundHours(remainingHours / Math.max(1, months.length - months.indexOf(monthKey)));
      monthBudget = Math.min(monthBudget, remainingHours);

      for (const { resource, avail } of byAvailability) {
        if (monthBudget <= 0) break;
        const rid = resource.resourceId;
        const assignedHours = Math.min(monthBudget, avail);
        if (assignedHours <= 0) continue;

        const serverSlots = breakdownCache[rid]?.[monthKey] ?? [];
        const distribution = distributeFromServerSlots(serverSlots, assignedHours, businessHoursStart, businessHoursEnd);
        const planned = distribution && finaliseDistribution(distribution, rid, capacityFor);

        if (planned) {
          slots.push({
            id: slotId(),
            resourceId: String(rid),
            resourceName: resource.resourceName,
            resourceRole: resource.resourceRole,
            resourcePhotoUrl: undefined,
            softSignalsMet: resource.matchScore,
            month: monthLabel(monthKey),
            monthKey,
            startDate: planned.startDate,
            endDate: planned.endDate,
            hours: planned.hours,
            availableHours: Math.round(avail),
            matchReasons: [
              `Highest availability this month (${Math.round(avail)}h free)`,
              `${resource.matchScore} of ${criteriaTotal} criteria met`,
              resource.resourceRole,
              ...planned.dailyBreakdown,
            ],
            dailyBreakdown: planned.dailyAllocations,
            roleFallback: roleFallbackWarning,
          });
          monthBudget = roundHours(monthBudget - planned.hours);
          remainingHours = roundHours(remainingHours - planned.hours);
        }
      }
    }

    if (slots.length === 0) return null;

    const covered = roundHours(totalHours - remainingHours);
    const uniqueResources = new Set(slots.map(s => s.resourceId)).size;

    return {
      id: 'sc-fastest-start',
      strategy: 'fastest-start',
      label: 'Fastest Start',
      description: 'Maximises availability — gets work started as soon as possible',
      icon: 'Zap',
      totalHours: covered,
      slots,
      coveragePercent: Math.round((covered / totalHours) * 100),
      resourceCount: uniqueResources,
      earliestStart: slots[0].startDate,
      latestEnd: slots[slots.length - 1].endDate,
      pros: [
        'Work begins at the earliest possible date',
        'Maximises use of available capacity right now',
        'Ideal when the project start date is non-negotiable',
      ],
      cons: [
        ...(request.skills.length > 0
          ? ['May assign resources with slightly lower skill match scores']
          : []),
        'Availability-first approach may not respect role preferences strictly',
      ],
    };
  }

  const scenarios: PlanScenario[] = [];

  const single = buildSingleResourceScenario();
  if (single) scenarios.push(single);

  const spread = buildSpreadLoadScenario();
  if (spread) scenarios.push(spread);

  const fastest = buildFastestStartScenario();
  if (fastest) scenarios.push(fastest);

  const resourceUtilisation = Object.keys(utilisationByResourceId).length > 0
    ? utilisationByResourceId
    : undefined;
  const resourceDailyUtilisation = Object.keys(utilisationDaysByResourceId).length > 0
    ? utilisationDaysByResourceId
    : undefined;

  // The free windows each measured resource has across the whole request window.
  // The drawer re-lays a slot's days onto them when a planner reassigns the slot,
  // so a manual resource change is fitted to the same availability the suggestion
  // was built from rather than keeping the previous resource's windows.
  const resourceAvailability: Record<string, DailyAvailabilitySlot[]> = {};
  for (const r of availabilityPlanResult) {
    if (r.dailyBreakdown.length > 0) {
      resourceAvailability[String(r.resourceId)] = r.dailyBreakdown;
    }
  }

  return scenarios.map(s => ({
    ...s,
    resourceUtilisation,
    resourceDailyUtilisation,
    resourceAvailability: Object.keys(resourceAvailability).length > 0
      ? resourceAvailability
      : undefined,
    ...(planAvailabilityContext
      ? { planAvailabilityContext, resourceMonthlyUtilisation: utilisationMonthsByResourceId }
      : {}),
  }));
}
